import type { FlowCardsResponse, FlowCommentsResponse, FlowVersionsResponse } from "@/lib/pb-types";
import { isOffline } from "@/features/workers/model";

/**
 * Flows: agents hand Pipe finished work to read and decide on, one card per
 * task, and Pipe hands agents work as queued cards whose v1 is the brief. The lifecycle itself lives in `pb_hooks/flows-lib.js`; this file holds
 * what the screen derives from it, plus the sandbox document for live components.
 */

export type FlowBlock =
  | { type: "markdown"; text: string }
  | { type: "image" | "video"; asset?: string; filename?: string; url?: string; caption?: string }
  | { type: "html"; html: string; title?: string };

export type FlowAd = {
  product: string;
  audience: string;
  hook: string;
  caption: string;
  headline: string;
  reasoning: string;
  creative: FlowBlock[];
};

export type FlowCitation = { text: string; source: string; version?: number; note?: string };

/**
 * A citation while Pipe writes his decision. `parked` (never sent) tucks one away into the
 * stack above the composer once he is done with it; only the open one shows its editor.
 */
export type DraftCitation = FlowCitation & { parked?: boolean };

/** A new citation becomes the open one; every other is parked. A passage already cited reopens. */
export function openNewCitation(list: readonly DraftCitation[], citation: FlowCitation): DraftCitation[] {
  if (list.some((item) => item.text === citation.text)) return reopenCitation(list, citation.text);
  return [...list.map((item) => ({ ...item, parked: true })), { ...citation, parked: false }];
}

/** Opens one citation for editing and parks the rest. */
export function reopenCitation(list: readonly DraftCitation[], text: string): DraftCitation[] {
  return list.map((item) => ({ ...item, parked: item.text !== text }));
}

export function parkCitation(list: readonly DraftCitation[], text: string): DraftCitation[] {
  return list.map((item) => (item.text === text ? { ...item, parked: true } : item));
}

/** What goes to the server: the citations without the composer's own state. */
export function sentCitations(list: readonly DraftCitation[]): FlowCitation[] {
  return list.map(({ parked: _parked, ...citation }) => citation);
}

/** 12.43 → "0:12.4", 3723.5 → "1:02:03.5": a point in a video to a tenth of a second. */
export function formatVideoTime(seconds: number): string {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds * 10) / 10 : 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = (total % 60).toFixed(1).padStart(4, "0");
  return hours ? `${hours}:${String(minutes).padStart(2, "0")}:${rest}` : `${minutes}:${rest}`;
}

/** The citation for one moment of a video: its name and the time, for the agent to find the frame. */
export function videoCitation(name: string, seconds: number, version?: number): FlowCitation {
  return { text: `${name || "วิดีโอ"} @ ${formatVideoTime(seconds)}`, source: "วิดีโอ", version };
}

export type FlowCard = FlowCardsResponse;
export type FlowVersion = FlowVersionsResponse<FlowAd, FlowBlock[]>;
export type FlowComment = FlowCommentsResponse<FlowCitation[]>;
/** What Pipe can pick in the decision bar. */
export type FlowDecision = "approve" | "changes" | "reject";
/** What a card or comment can carry: the three above, or Pipe's answer to an agent's question. */
export type FlowOutcome = FlowDecision | "answer";

/**
 * รอผม first: it is what Pipe opens Flows to check, and the default tab. `waiting` is work no
 * one holds yet (queued, or decided and waiting for its agent), `working` is work someone holds.
 * The same four slots read differently by group (TAB_LABELS): a machine's queue, or the cards
 * Pipe still has to hand to a chat.
 */
export const FLOW_TABS = ["review", "waiting", "working", "done"] as const;
export type FlowTab = (typeof FLOW_TABS)[number];

/** Tab ids from before the groups, still in old links: `queue` was queued work, `agent` decided and picked work. */
export const LEGACY_TABS = ["queue", "agent"] as const;
export type RouteTab = FlowTab | (typeof LEGACY_TABS)[number];
export const ROUTE_TABS: readonly RouteTab[] = [...FLOW_TABS, ...LEGACY_TABS];

/**
 * The tab a URL means. An old `agent` link names a card that is now either waiting or held, so
 * the card itself decides; without one it lands where Pipe acts, the waiting tab.
 */
export function resolveTab(raw: RouteTab, card?: Pick<FlowCard, "status"> | null): FlowTab {
  if (raw === "queue") return "waiting";
  if (raw === "agent") return card ? tabOf(card) : "waiting";
  return raw;
}

/**
 * Who moves a category's cards on: a Worker machine that picks it by itself (Workers page), or
 * Pipe, who hands each card to a chat. `all` is the combined view of every live category.
 */
export type FlowGroup = "machine" | "chat";
export type FlowView = FlowGroup | "all";

export const GROUP_LABEL: Record<FlowGroup, string> = { machine: "🤖 เครื่องรับงานเอง", chat: "💬 ต้องส่งให้แชท" };

export const TAB_LABELS: Record<FlowView, Record<FlowTab, string>> = {
  all: { review: "รอผม", waiting: "รอหยิบ", working: "กำลังทำ", done: "เสร็จ" },
  machine: { review: "รอคุณตรวจ", waiting: "คิวตัด", working: "กำลังตัด", done: "เสร็จ" },
  chat: { review: "รอผม", waiting: "รอส่งให้แชท", working: "แชทกำลังทำ", done: "เสร็จ" },
};
/** The combined view's names, for places that show one card's tab outside Flows (search). */
export const TAB_LABEL: Record<FlowTab, string> = TAB_LABELS.all;

/** What an empty tab says. */
export const EMPTY_LABELS: Record<FlowView, Record<FlowTab, string>> = {
  all: {
    review: "ไม่มีการ์ดรอคุณอยู่",
    waiting: "ไม่มีงานรอ agent หยิบ · ส่งจาก Tasks หรือ Inbox ด้วยปุ่ม ส่งให้ agent",
    working: "ไม่มี agent กำลังทำการ์ดอยู่",
    done: "ยังไม่มีการ์ดในแท็บนี้",
  },
  machine: {
    review: "ไม่มีเวอร์ชันหรือคำถามรอคุณตรวจ",
    waiting: "คิวว่าง · เครื่องจะหยิบงานใหม่ที่ส่งเข้าหมวดนี้เอง",
    working: "เครื่องยังไม่ได้ตัดการ์ดของคุณอยู่",
    done: "ยังไม่มีการ์ดในแท็บนี้",
  },
  chat: {
    review: "ไม่มีการ์ดรอคุณอยู่",
    waiting: "ไม่มีการ์ดรอส่งให้แชท",
    working: "ไม่มีแชทกำลังทำการ์ดอยู่",
    done: "ยังไม่มีการ์ดในแท็บนี้",
  },
};
export const EMPTY_LABEL: Record<FlowTab, string> = EMPTY_LABELS.all;
export const DECISION_LABEL: Record<FlowDecision, string> = {
  approve: "Approve",
  changes: "Request changes",
  reject: "Reject",
};
export const OUTCOME_LABEL: Record<FlowOutcome, string> = { ...DECISION_LABEL, answer: "Answered" };

/**
 * Where an approval sends the work, for the Approve button: a card that came from a queue
 * fills its item there (drafts-lib.js), a queued Task is completed. Null when it goes nowhere.
 */
export function approveTarget(card: Pick<FlowCard, "origin" | "source_kind">): { label: string; detail: string } | null {
  if (card.origin !== "owner") return null;
  switch (card.source_kind) {
    case "organic":
      return { label: "Organic Queue", detail: "วิดีโอและแคปชันจะเติมลงชิ้นงานเดิมใน Organic Queue แล้วกดเลื่อนเป็น \"พร้อมลง\" ที่นั่น" };
    case "ad":
      return { label: "Ads Queue", detail: "วิดีโอ แคปชัน และหัวข้อจะเติมลงร่างแอดเดิมใน Ads Queue แล้วกด \"ย้ายเข้าคิว\" ที่นั่น" };
    case "task":
      return { label: "ปิด Task", detail: "Task ที่ส่งงานนี้มาจะถูกติ๊กว่าเสร็จ" };
    default:
      return null;
  }
}

export function tabOf(card: Pick<FlowCard, "status">): FlowTab {
  switch (card.status) {
    case "review": return "review";
    case "done": return "done";
    case "picked": return "working";
    default: return "waiting";
  }
}

/** When a waiting card started waiting: its decision, or for queued work, when it was handed out. */
export function waitingSince(card: Pick<FlowCard, "status" | "decided_at" | "created" | "updated">): string {
  if (card.status === "decided") return card.decided_at || card.updated;
  return card.created;
}

/**
 * Waiting work in the order an agent's next pick takes it (flows-lib.js pick → nextCard):
 * decisions first, oldest decision first, then queued work, oldest first.
 */
export function queueOrder<T extends Pick<FlowCard, "status" | "decided_at" | "created" | "updated">>(cards: readonly T[]): T[] {
  const rank = (card: T) => (card.status === "decided" ? 0 : 1);
  return [...cards].sort((a, b) => rank(a) - rank(b) || waitingSince(a).localeCompare(waitingSince(b)));
}

/**
 * Oldest first while it waits on someone, so nothing sinks: the waiting tab in pick order, the
 * held cards longest-held first (the ones that may be stuck); newest first once it is done.
 */
export function sortForTab(cards: readonly FlowCard[], tab: FlowTab): FlowCard[] {
  if (tab === "waiting") return queueOrder(cards);
  const sorted = [...cards];
  if (tab === "review") sorted.sort((a, b) => a.created.localeCompare(b.created));
  else if (tab === "working") sorted.sort((a, b) => (a.picked_at || a.updated).localeCompare(b.picked_at || b.updated));
  else sorted.sort((a, b) => b.updated.localeCompare(a.updated));
  return sorted;
}

/** The deep link that opens one card on its own tab, which is the only place Flows shows it. */
export function cardLink(card: Pick<FlowCard, "id" | "status">): string {
  return `/flows?tab=${tabOf(card)}&card=${encodeURIComponent(card.id)}`;
}

/**
 * The cards one piece of work moved between (flows-lib.js replaceCard): the card this one took
 * over from ("แทน: <old title>") and, on a card that was replaced, the one that took over
 * ("ถูกแทนด้วย <new title>"). Flows shows only the cards it lists, so one it does not have
 * (older than the list reaches) keeps a plain name and no link.
 */
export type ReplaceLink = { kind: "replaces" | "replaced_by"; id: string; label: string; href: string | null };

export function replaceLinks(card: Pick<FlowCard, "replaces" | "replaced_by">, cards: readonly FlowCard[]): ReplaceLink[] {
  const link = (kind: ReplaceLink["kind"], id: string | undefined, prefix: string, fallback: string): ReplaceLink[] => {
    if (!id) return [];
    const other = cards.find((item) => item.id === id);
    return [{ kind, id, label: `${prefix}${other?.title || fallback}`, href: other ? cardLink(other) : null }];
  };
  return [...link("replaces", card.replaces, "แทน: ", "การ์ดเดิม"), ...link("replaced_by", card.replaced_by, "ถูกแทนด้วย ", "การ์ดใหม่")];
}

/** Where to land after deciding: the card that took its place, else the one before it. */
export function nextAfter(cards: readonly FlowCard[], id: string): string | null {
  const index = cards.findIndex((card) => card.id === id);
  if (index < 0) return cards[0]?.id ?? null;
  return cards[index + 1]?.id ?? cards[index - 1]?.id ?? null;
}

/** A reason can sit in the overall comment or on any one citation. */
export function canSubmit(decision: FlowDecision | null, comment: string, citations: readonly FlowCitation[] = []): boolean {
  if (!decision) return false;
  return decision === "approve" || comment.trim().length > 0 || citations.some((citation) => citation.note?.trim());
}

/** What Pipe pastes into a chat so an agent takes this card. Queued work is a brief, not a thread to answer. */
export function pickCommand(card: Pick<FlowCard, "id" | "title" | "status">): string {
  if (card.status === "queued") {
    return `ใช้ kobo_flow_pick หยิบการ์ด Flows id ${card.id} ("${card.title}") อ่าน brief (v1) กับไฟล์แนบ แล้วทำตาม kobo_flows_instructions`;
  }
  return `ใช้ kobo_flow_pick หยิบการ์ด Flows id ${card.id} ("${card.title}") อ่าน thread กับ citation ทั้งหมด แล้วทำตาม kobo_flows_instructions`;
}

/** Everything a card shows as text, ad fields first, in reading order. */
export function adFields(ad: FlowAd): { label: string; value: string }[] {
  return [
    { label: "สินค้า", value: ad.product },
    { label: "กลุ่มเป้าหมาย", value: ad.audience },
    { label: "Hook", value: ad.hook },
    { label: "Caption", value: ad.caption },
    { label: "Headline", value: ad.headline },
    { label: "เหตุผล", value: ad.reasoning },
  ];
}

/**
 * The window message of the frame's helper script. Any script in the frame can
 * send it too, so what it says is never trusted beyond the frame's own box.
 */
export const FRAME_MESSAGE = "busee-flow-frame";

export type FrameMessage =
  | { source: typeof FRAME_MESSAGE; kind: "height"; height: number }
  | { source: typeof FRAME_MESSAGE; kind: "escape" }
  | { source: typeof FRAME_MESSAGE; kind: "selection"; text: string; rect: { left: number; top: number; bottom: number } | null };

export function readFrameMessage(data: unknown): FrameMessage | null {
  if (typeof data !== "object" || data === null) return null;
  const message = data as Partial<FrameMessage>;
  if (message.source !== FRAME_MESSAGE) return null;
  if (message.kind === "height" && Number.isFinite(message.height)) return message as FrameMessage;
  if (message.kind === "escape") return message as FrameMessage;
  if (message.kind === "selection" && typeof message.text === "string") {
    const rect = message.rect;
    if (rect !== null && ![rect?.left, rect?.top, rect?.bottom].every(Number.isFinite)) return null;
    return message as FrameMessage;
  }
  return null;
}

// Measures the body, not the document: the document is never shorter than the
// frame, so a frame could only ever grow. The body's scroll height counts what overflows it
// (a tall SVG or grid), and `flow-root` keeps floats and margins inside it, so the frame is
// never a few pixels short. The frame itself never scrolls (a nested scroller rubber-bands on
// a Mac and traps the wheel) unless its height was capped and content really is hidden; the
// check waits until the parent has resized the frame. Reports that height, and its text selection,
// so a passage can be cited: the parent has no other way to see into a frame
// without same-origin rights.
const FRAME_HELPER = `(function(){
var post=function(m){m.source=${JSON.stringify(FRAME_MESSAGE)};parent.postMessage(m,"*")};
var fitTimer,fit=function(){var d=document.documentElement;d.style.overflowY=d.scrollHeight>innerHeight+2?"auto":"hidden"};
var size=function(){var b=document.body;if(!b)return;post({kind:"height",height:Math.max(b.getBoundingClientRect().height,b.scrollHeight)});clearTimeout(fitTimer);fitTimer=setTimeout(fit,300)};
var timer;
document.addEventListener("selectionchange",function(){clearTimeout(timer);timer=setTimeout(function(){
var s=getSelection(),t=s?String(s).trim():"",r=null;
if(t&&s.rangeCount){var b=s.getRangeAt(0).getBoundingClientRect();r={left:b.left,top:b.top,bottom:b.bottom}}
post({kind:"selection",text:t,rect:r})},120)});
addEventListener("load",size);
addEventListener("resize",fit);
document.addEventListener("load",size,true);
document.addEventListener("keydown",function(e){if(e.key==="Escape")post({kind:"escape"})});
if(window.ResizeObserver){addEventListener("DOMContentLoaded",function(){new ResizeObserver(size).observe(document.body)})}
})();`;

// The frame has an opaque origin (no `allow-same-origin`), so agent HTML cannot
// read Kobo or its session whatever this says. The CSP only stops `fetch` and
// forms: loading from https stays open for chart libraries and images, which
// also means a component could beacon its own content or navigate itself.
// Components come from Pipe's own agents, so that is accepted, not prevented.
const FRAME_CSP =
  "default-src 'none'; script-src 'unsafe-inline' https:; style-src 'unsafe-inline' https:; img-src data: blob: https:; media-src data: blob: https:; font-src data: https:; form-action 'none'";

function escapeAttribute(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

export function buildFrameDocument(html: string): string {
  return [
    "<!doctype html><html><head>",
    `<meta http-equiv="Content-Security-Policy" content="${escapeAttribute(FRAME_CSP)}">`,
    "<style>html,body{margin:0}html{overflow-y:hidden}body{display:flow-root;padding:12px;font-family:sans-serif;background:#fff;color:#111}</style>",
    `<script>${FRAME_HELPER}</script>`,
    "</head><body>",
    html,
    "</body></html>",
  ].join("");
}

/** The cards either side of `id` in the list on screen, for ← → in an open card. */
export function neighbours<T extends { id: string }>(listed: readonly T[], id: string): { prev: T | null; next: T | null; index: number } {
  const index = listed.findIndex((card) => card.id === id);
  if (index < 0) return { prev: null, next: null, index };
  return { prev: listed[index - 1] ?? null, next: listed[index + 1] ?? null, index };
}

/**
 * Whether a keypress belongs to whatever has focus rather than to card paging: typing,
 * a select, a slider or a video's own controls keep their arrow keys.
 */
export function keyBelongsToField(target: EventTarget | null): boolean {
  if (!target || typeof (target as Element).closest !== "function") return false;
  const element = target as HTMLElement;
  if (element.isContentEditable) return true;
  return Boolean(element.closest("input, textarea, select, video, audio, [role='slider'], [data-video-player], [contenteditable='true'], .kobo-video"));
}

/** Machines that pick a Flows category by itself (Workers: categories), matched on the category key. */
export function workersFor<W extends { categories?: unknown }>(categoryKey: string, workers: readonly W[]): W[] {
  const keyOf = (name: string) => name.trim().toLowerCase().replace(/\s+/g, "-");
  return workers.filter((worker) => Array.isArray(worker.categories) && worker.categories.some((name) => typeof name === "string" && keyOf(name) === categoryKey));
}


/** A category with at least one machine picking it is the machine's; every other one waits on Pipe to hand it to a chat. */
export function groupOf<W extends { categories?: unknown }>(categoryKey: string, workers: readonly W[]): FlowGroup {
  return workersFor(categoryKey, workers).length ? "machine" : "chat";
}

/** The rail's name for a category: a cutter's queue reads as what it does. */
export function categoryLabel(name: string, group: FlowGroup): string {
  return group === "machine" && /^\s*cutter/i.test(name) ? "✂ ตัดคลิป" : name;
}

/** The rail's two groups in order, each with its live categories in their own order; an empty group is left out. */
export function groupCategories<C extends { key: string }, W extends { categories?: unknown }>(
  categories: readonly C[],
  workers: readonly W[],
): { group: FlowGroup; categories: C[] }[] {
  return (["machine", "chat"] as const)
    .map((group) => ({ group, categories: categories.filter((category) => groupOf(category.key, workers) === group) }))
    .filter((entry) => entry.categories.length > 0);
}

/** Cards that wait on Pipe in one category: to read (review), and, for a chat category, to hand to a chat (decided). */
export function categoryCounts(cards: readonly Pick<FlowCard, "category" | "status">[], categoryId: string): { review: number; handoff: number } {
  let review = 0;
  let handoff = 0;
  for (const card of cards) {
    if (card.category !== categoryId) continue;
    if (card.status === "review") review += 1;
    else if (card.status === "decided") handoff += 1;
  }
  return { review, handoff };
}

/** What a heartbeat row says about one machine, for its chip above a machine category. */
export type MachineWorker = HoldWorker & { key: string; name?: string; started_at?: string; current_owner?: string };

/** The Workers page's status (workers/model.ts workerStatus) for a heartbeat row of any shape. */
function machineStatus(worker: Pick<HoldWorker, "state" | "last_seen">, now: Date): "idle" | "working" | "error" | "offline" {
  if (isOffline(worker.last_seen, now)) return "offline";
  return worker.state === "working" || worker.state === "error" ? worker.state : "idle";
}

/** A card being cut now in a machine lane: whose it is ("" is the viewer's own) and who holds it. */
export type CuttingCard = { owner: string; title?: string; picked_by?: string };

type LaneCategory = { id: string; key: string };
type LaneCard = { category: string; status?: string; title?: string; picked_by?: string; user?: string };

/**
 * The picked cards in the lane of this key: the viewer's own (in his categories of that key) and
 * other members' (in their shared categories of that key, read since migration 1785801659). These
 * are the real cards, so two slots cutting two members' cards count as two whatever a heartbeat says.
 */
export function cuttingCards(
  key: string,
  own: { categories: readonly LaneCategory[]; cards: readonly LaneCard[] },
  shared: { categories: readonly (LaneCategory & { user?: string })[]; cards: readonly LaneCard[] },
): CuttingCard[] {
  const ownLanes = new Set(own.categories.filter((category) => category.key === key).map((category) => category.id));
  const sharedLanes = new Set(shared.categories.filter((category) => category.key === key).map((category) => category.id));
  const out: CuttingCard[] = [];
  for (const card of own.cards) {
    if (card.status === "picked" && ownLanes.has(card.category)) out.push({ owner: "", title: card.title, picked_by: card.picked_by });
  }
  for (const card of shared.cards) {
    if (card.status === "picked" && card.user && sharedLanes.has(card.category)) out.push({ owner: card.user, title: card.title, picked_by: card.picked_by });
  }
  return out;
}

/** One owner's share of what a machine cuts: `owner` "" is the viewer ("ของฉัน"). */
export type CuttingOwner = { owner: string; name: string; count: number };

export type MachineChip = {
  key: string;
  name: string;
  /** Reported within the Workers page's offline window (15 minutes). */
  alive: boolean;
  /** "กำลังตัด 2", "ว่าง", "ติดปัญหา", "ออฟไลน์", "ออฟไลน์ · ถือ 1". */
  load: string;
  /** Cards it holds in this lane now. */
  count: number;
  /** The same cards by owner, the viewer first: "ของฉัน 1 · พี่โจ้ 1". */
  owners: CuttingOwner[];
  /** The cards' titles, for the chip's tooltip. */
  title: string;
};

/** A card picked as "cutter@hermes-video" is held by the machine whose key is hermes-video. */
function heldBy(card: CuttingCard, key: string): boolean {
  const by = card.picked_by || "";
  return by === key || by.endsWith(`@${key}`);
}

/**
 * One chip per machine. Alive is the Workers page's own rule (workerStatus): a heartbeat within
 * OFFLINE_AFTER_MS, and the heartbeat says only that (online, offline, a problem). What it is
 * cutting comes from the lane's picked cards (cuttingCards): one machine gets them all; with
 * several, each gets the cards it picked, and a card none of them names goes to the first.
 */
export function machineChips(
  workers: readonly MachineWorker[],
  now: Date,
  cutting: readonly CuttingCard[] = [],
  names: readonly SharedOwner[] = [],
): MachineChip[] {
  const nameOf = new Map(names.map((entry) => [entry.id, entry.name]));
  const claimed = workers.map((worker) => cutting.filter((card) => workers.length === 1 || heldBy(card, worker.key)));
  const unclaimed = workers.length > 1 ? cutting.filter((card) => !workers.some((worker) => heldBy(card, worker.key))) : [];
  return workers.map((worker, index) => {
    const status = machineStatus(worker, now);
    const cards = index === 0 ? [...claimed[0]!, ...unclaimed] : claimed[index]!;
    const counts = new Map<string, number>();
    for (const card of cards) counts.set(card.owner, (counts.get(card.owner) ?? 0) + 1);
    const owners = [...counts.entries()]
      .map(([owner, count]) => ({ owner, name: owner ? nameOf.get(owner) || "สมาชิก" : "ของฉัน", count }))
      .sort((a, b) => (a.owner === "" ? -1 : b.owner === "" ? 1 : a.name.localeCompare(b.name, "th") || a.owner.localeCompare(b.owner)));
    const count = cards.length;
    const load =
      status === "offline"
        ? count ? `ออฟไลน์ · ถือ ${count}` : "ออฟไลน์"
        : status === "error"
          ? count ? `ติดปัญหา · กำลังตัด ${count}` : "ติดปัญหา"
          : count ? `กำลังตัด ${count}` : "ว่าง";
    return {
      key: worker.key,
      name: worker.name || worker.key,
      alive: status !== "offline",
      load,
      count,
      owners,
      title: cards.map((card) => card.title || "").filter(Boolean).join(" · "),
    };
  });
}

/** Another org member whose cutter work Flows shows read-only (flows-lib.js sharedOwners). */
export type SharedOwner = { id: string; name: string };

/** What a shared lane needs of a category and of a card: whose it is. */
type OwnedCategory = { id: string; key: string; user?: string; archived_at?: string };
type OwnedCard = { category: string; user?: string };

/**
 * The other members with cutter work in the lane of this key (migration 1785801659: a category a
 * Worker picks is shared with the org), by name: each with a live category of that key holding at
 * least one card. A name the server did not give reads "สมาชิก"; never an email.
 */
export function laneOwners(
  key: string,
  categories: readonly OwnedCategory[],
  cards: readonly OwnedCard[],
  owners: readonly SharedOwner[],
): SharedOwner[] {
  const lanes = new Set(categories.filter((category) => category.key === key && !category.archived_at && category.user).map((category) => category.id));
  const ids = new Set(cards.filter((card) => card.user && lanes.has(card.category)).map((card) => card.user as string));
  const names = new Map(owners.map((owner) => [owner.id, owner.name]));
  return [...ids].map((id) => ({ id, name: names.get(id) || "สมาชิก" })).sort((a, b) => a.name.localeCompare(b.name, "th") || a.id.localeCompare(b.id));
}

/** One member's cards in the lane of this key. */
export function laneCards<C extends OwnedCard>(key: string, owner: string, categories: readonly OwnedCategory[], cards: readonly C[]): C[] {
  const lanes = new Set(categories.filter((category) => category.key === key && category.user === owner).map((category) => category.id));
  return cards.filter((card) => card.user === owner && lanes.has(card.category));
}

/** Shared lanes the viewer has no live category of his own for: one category each, to stand for its key in the rail. */
export function sharedOnlyLanes<C extends OwnedCategory>(ownKeys: ReadonlySet<string>, categories: readonly C[]): C[] {
  const seen = new Set<string>();
  return categories.filter((category) => {
    if (category.archived_at || ownKeys.has(category.key) || seen.has(category.key)) return false;
    seen.add(category.key);
    return true;
  });
}

/** "รอพี่โจ้ตรวจ", "รอ Pipe ตรวจ": a Latin name gets spaces around it, Thai runs on. */
function around(prefix: string, name: string, suffix: string): string {
  return /^[\x20-\x7e]+$/.test(name) ? `${prefix} ${name} ${suffix}`.trim() : `${prefix}${name}${suffix}`;
}

/** A machine lane's tabs read for another member's cards. */
export function ownerTabLabels(name: string): Record<FlowTab, string> {
  return { ...TAB_LABELS.machine, review: around("รอ", name, "ตรวจ") };
}

export function ownerEmptyLabels(name: string): Record<FlowTab, string> {
  return {
    review: around("ไม่มีเวอร์ชันหรือคำถามรอ", name, "ตรวจ"),
    waiting: around("คิวตัดของ", name, "ว่าง"),
    working: around("เครื่องยังไม่ได้ตัดการ์ดของ", name, "อยู่"),
    done: "ยังไม่มีการ์ดในแท็บนี้",
  };
}

/**
 * Whether an Approve carries something to do: comment text or a cited passage with a note.
 * The server then leaves handed-out work for an agent instead of closing it (flows-lib.js decide).
 */
export function approvalHasComment(comment: string, citations: readonly { note?: string }[]): boolean {
  return Boolean(comment.trim()) || citations.some((citation) => Boolean(citation.note?.trim()));
}

/** Handed-out work Pipe approved with a comment: an agent carries the comment out, then it closes. */
export function awaitsCommentWork(card: Pick<FlowCard, "origin" | "decision" | "status">): boolean {
  return card.origin === "owner" && card.decision === "approve" && (card.status === "decided" || card.status === "picked");
}

/** A held card with no sign of life for this long gets the "ค้าง?" tag. */
export const STALE_HOLD_MS = 2 * 60 * 60_000;

/** What a held card needs from a machine's heartbeat row (Workers) to know it is still on it. */
export type HoldWorker = {
  state?: string;
  last_seen?: string;
  current_card?: string;
  current_title?: string;
  /** One job per slot of a machine that runs several cards at once (workers.jobs). */
  jobs?: Record<string, { card?: string; title?: string; seen_at?: string; started_at?: string }> | null;
};

/** "2026-11-03 10:00:00.000Z" (PocketBase) or ISO; an empty string is no date. */
function stamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = new Date(value.replace(" ", "T")).getTime();
  return Number.isNaN(time) ? null : time;
}

/** A short Thai age: "ไม่ถึงนาที", "12 นาที", "3 ชม.", "2 วัน". */
export function shortAge(ms: number): string {
  const minutes = Math.floor(Math.max(0, ms) / 60_000);
  if (minutes < 1) return "ไม่ถึงนาที";
  if (minutes < 60) return `${minutes} นาที`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ชม.`;
  return `${Math.floor(hours / 24)} วัน`;
}

/**
 * Whether a machine reported working on this card within STALE_HOLD_MS. A worker names its
 * card in its start heartbeat (`current_card`, or its slot's job in `jobs`); one that sent only
 * the title is matched on it.
 * A chat holder has no heartbeat, so its card has no sign of life beyond its pick time.
 */
export function holderAlive(card: Pick<FlowCard, "id" | "title">, workers: readonly HoldWorker[], now: Date): boolean {
  return workers.some((worker) => {
    // A slot job is alive while its slot pings, even when another slot left the machine in error.
    const jobs = worker.jobs && typeof worker.jobs === "object" ? Object.values(worker.jobs) : [];
    const slotted = jobs.some((job) => {
      const seen = stamp(job?.seen_at || job?.started_at);
      if (!job || seen === null || now.getTime() - seen > STALE_HOLD_MS) return false;
      return job.card ? job.card === card.id : Boolean(job.title) && job.title === card.title;
    });
    if (slotted) return true;
    if (worker.state !== "working") return false;
    const seen = stamp(worker.last_seen);
    if (seen === null || now.getTime() - seen > STALE_HOLD_MS) return false;
    if (worker.current_card) return worker.current_card === card.id;
    return Boolean(worker.current_title) && worker.current_title === card.title;
  });
}

/**
 * How long a picked card has been held, and whether it looks abandoned: held over two hours
 * with no heartbeat for it in that time. Null for a card nobody holds.
 */
export function holdOf(
  card: Pick<FlowCard, "id" | "title" | "status" | "picked_at">,
  workers: readonly HoldWorker[],
  now: Date,
): { label: string; stale: boolean } | null {
  if (card.status !== "picked") return null;
  const since = stamp(card.picked_at);
  if (since === null) return null;
  const held = now.getTime() - since;
  return { label: `ถือมาแล้ว ${shortAge(held)}`, stale: held > STALE_HOLD_MS && !holderAlive(card, workers, now) };
}

/** "รอมาแล้ว 3 ชม.": how long a waiting card has sat since it started waiting (waitingSince). */
export function waitedLabel(card: Pick<FlowCard, "status" | "decided_at" | "created" | "updated">, now: Date): string {
  const since = stamp(waitingSince(card));
  return since === null ? "" : `รอมาแล้ว ${shortAge(now.getTime() - since)}`;
}

/* --- The thread as a chat (CardPane.tsx Thread) --------------------------------------- */

/** Longest piece an agent's note may have and still be shown as a bullet. */
export const BODY_ITEM_MAX = 160;

/**
 * An agent's note as a short list, only when the agent already separated its items: numbered
 * "(1) … (2) …" segments (anything before "(1)" is the lead), or pieces joined by " · " (a
 * first piece that is only a version tag such as "v2" is the lead). Conservative on purpose:
 * null, so the note shows as written, for a note with line breaks, fewer than two items, an
 * empty or over-long piece, or numbers out of order. Pipe's own comments are never split.
 */
export function bodyItems(body: string): { lead: string; items: string[] } | null {
  const text = body.trim();
  if (!text || text.includes("\n")) return null;
  const fits = (items: readonly string[]) => items.length >= 2 && items.every((item) => item.length > 0 && item.length <= BODY_ITEM_MAX);

  const marks = [...text.matchAll(/\((\d{1,2})\)/g)];
  if (marks.length >= 2) {
    if (!marks.every((mark, index) => Number(mark[1]) === index + 1)) return null;
    const at = marks.map((mark) => mark.index ?? 0);
    const lead = text.slice(0, at[0]).trim();
    const items = marks.map((mark, index) => text.slice(at[index] + mark[0].length, at[index + 1] ?? text.length).trim());
    return fits(items) && lead.length <= BODY_ITEM_MAX ? { lead, items } : null;
  }

  const pieces = text.split(" · ").map((piece) => piece.trim());
  const lead = /^[vr]\d+[a-z]?$/i.test(pieces[0]) ? pieces[0] : "";
  const items = lead ? pieces.slice(1) : pieces;
  return fits(items) ? { lead, items } : null;
}

/**
 * A decision or an edit an agent made for Pipe on his word in a chat ends with
 * "— <agent> ทำแทนตามที่สั่งในแชท: “<what he said>”" (flows-lib.js behalfLine). Split off, so
 * the bubble shows his comment and a small "ผ่าน <agent>" note under it.
 */
export function behalfOf(body: string): { text: string; agent: string; said: string } | null {
  const match = /(?:^|\n\n)— ([^\n]+?) ทำแทนตามที่สั่งในแชท: “([\s\S]*)”\s*$/.exec(body);
  if (!match) return null;
  return { text: body.slice(0, match.index).trim(), agent: match[1], said: match[2] };
}

/**
 * The lines the server writes itself when a card is released, closed or replaced
 * (flows-lib.js release, close, replaceCard): shown as small centred notes, not as messages.
 * Recognised by their exact wording on a comment with no decision and no citations.
 */
const SYSTEM_LINES = [
  /^[^\n]{1,120} ปลดการ์ดจาก [^\n]+ (?:คืนคิว agent|ให้รอ agent มาหยิบใหม่)$/,
  /^[^\n]{1,120} ปิดการ์ดนี้(?: \(ปลดจาก [^\n]+\))?(?:\nเหตุผล: [\s\S]*)?$/,
  /^[^\n]{1,120} แทนการ์ดนี้ด้วยการ์ดใหม่ “[^\n]*” \([a-z0-9]+\)(?: · ปลดจาก [^\n]+)?$/,
];

export function isSystemLine(comment: Pick<FlowComment, "body" | "decision" | "citations">): boolean {
  if (comment.decision || (comment.citations ?? []).length) return false;
  const body = (comment.body ?? "").trim();
  return SYSTEM_LINES.some((line) => line.test(body));
}

/**
 * The tag a comment carries in its bubble. Pipe's comments come from decide (with its decision),
 * answer (none), or the release/close/replace lines; so one of his with no decision that is not
 * such a line is his answer to an agent's question (flows-lib.js answer).
 */
export function commentOutcome(comment: Pick<FlowComment, "author" | "body" | "decision" | "citations">): FlowOutcome | null {
  if (comment.decision) return comment.decision;
  return comment.author === "owner" && !isSystemLine(comment) ? "answer" : null;
}

/** The letter on an agent's avatar: ✂ for a cutter, otherwise the label's first letter. */
export function agentInitial(label: string): string {
  const name = label.trim();
  if (/cutter/i.test(name)) return "✂";
  const first = Array.from(name)[0];
  return first ? first.toUpperCase() : "A";
}

export type ThreadRow =
  | { kind: "version"; version: number; created: string }
  | { kind: "comment"; comment: FlowComment };

/** The thread in order, with a divider before the first comment of each new version. */
export function threadRows(comments: readonly FlowComment[]): ThreadRow[] {
  const rows: ThreadRow[] = [];
  let shown: number | null = null;
  for (const comment of comments) {
    if (comment.version !== shown) {
      rows.push({ kind: "version", version: comment.version, created: comment.created });
      shown = comment.version;
    }
    rows.push({ kind: "comment", comment });
  }
  return rows;
}
